// overrideStatus.js — single source for status-override vocabulary and the
// EFFECTIVE-STATUS precedence (override wins over the scored verdict).
//
// Consumers: the dashboard (SecureShield365.js), the Excel export
// (FindingsExport.js), and — through the disposition helpers — the PDF export.
// Same reason severity.js and tiers.js exist: three surfaces render the same
// disposition and must never disagree about what a row's status IS.
//
// Data source: override records arrive on /api/report as `report.overrides`,
// keyed by setting_id, each `{ override, reason, updated_by, updated_at }`
// (backend/shared/override_client.py get_overrides_for_slug). The dashboard
// holds them in `settingDetails`. There is no revisit_by on an override record
// — that column belongs to setting notes (pc_settingnote).

import { CREDIT_OVERRIDES } from "./composition";

// ── Disposition vocabulary ──
// Override disposition keys → human label + accent color. Matches the colors
// used by the per-setting status badges (teal = Customer Managed Solution, etc.).
export const DISPOSITION_LABELS = {
  planned: "Planned",
  risk_accepted: "Risk Accepted",
  customer_managed_solution: "Customer Managed",
  customer_remediated: "Customer Remediated",
};
export const DISPOSITION_COLORS = {
  planned: "#818CF8",
  risk_accepted: "#FBBF24",
  customer_managed_solution: "#14B8A6",
  customer_remediated: "#53c46d",
};
export const dispositionLabel = (key) => DISPOSITION_LABELS[key] || key || "—";
export const dispositionColor = (key) => DISPOSITION_COLORS[key] || "#a8bddc";

export const STATUS_OVERRIDE_NOTE_LIFECYCLE_OPTIONS = [
  {
    value: "Ongoing",
    label: "Permanent",
    helper: "Keeps the note active until someone removes or changes it.",
  },
  {
    value: "Short-term",
    label: "Short-term",
    helper: "Requires a revisit date so the team knows when to check back.",
  },
];

export function statusOverrideNoteLifecycleLabel(value) {
  return STATUS_OVERRIDE_NOTE_LIFECYCLE_OPTIONS.find(o => o.value === value)?.label
    || value
    || "—";
}

// ── Severity-tile disposition — ONE classifier, four surfaces ──
// The Overview severity cards, the "Critical + High Open" KPI, the RecsPage
// Needs Action filter those tiles click into, and the PDF severity breakdown
// all answer the same question: does this row still need action, has it been
// handled, or does it not belong on the tile at all? Before 2026-08-14 each
// surface answered it separately and they disagreed twice over (operator
// report + live evidence, characterized in SecureShield365.severityCards.test.js):
//
//   * a never-evaluated row kept status "open" and leaked into the headline,
//     while every list it clicked into hid it (the 2026-07-28 rule: rows that
//     earn nothing count nothing), and
//   * customer_managed_solution was subtracted from the headline but missing
//     from the addressed line, so those rows appeared in NO number at all.
//
// Three buckets, mutually exclusive, covering every row:
//   EXCLUDED  — never assessed. Earned nothing, so it counts nothing, and no
//               override changes that: an override on a row that was never
//               evaluated still describes nothing measured.
//   ADDRESSED — a credit-granting override says the customer has handled it
//               (risk_accepted / customer_managed_solution / customer_remediated
//               — CREDIT_OVERRIDES, the set that already drives score credit).
//   OPEN      — everything else, "planned" included: scheduling remediation
//               grants no score credit, so the work is still outstanding.
//
// OPEN and ADDRESSED are complements over the assessed rows, which is what
// makes the tile invariant hold by construction: A TILE'S NUMBER MUST EQUAL
// THE "Showing N" COUNT ITS CLICK LANDS ON.
//
// CREDIT_OVERRIDES is imported, never redefined — composition.js owns it and
// the Risk-Adjusted score math reads the same set.
export const TILE_OPEN = "open";
export const TILE_ADDRESSED = "addressed";
export const TILE_EXCLUDED = "excluded";

export function tileDisposition(verdict, override) {
  if (verdict === "not_assessed") return TILE_EXCLUDED;
  return CREDIT_OVERRIDES.includes(override) ? TILE_ADDRESSED : TILE_OPEN;
}

export const countsAsOpen = (verdict, override) =>
  tileDisposition(verdict, override) === TILE_OPEN;
export const countsAsAddressed = (verdict, override) =>
  tileDisposition(verdict, override) === TILE_ADDRESSED;

// ── Effective-status precedence ──
// Disposition key → statusBadge() key. This map IS the precedence rule
// "overrides win" (operator call, 2026-07-28): an explicit business decision
// outranks both the scored verdict and the Not Assessed gap.
export const OVERRIDE_STATUS_KEY = {
  risk_accepted: "exception",
  customer_remediated: "pending",
  planned: "planned",
  customer_managed_solution: "managed_solution",
};

// Plain-text equivalents of the statusBadge() labels, glyph stripped. The UI
// pills carry leading glyphs ("✓ Satisfied", "– Not Assessed") that are noise
// in a spreadsheet cell and break Excel's column filtering/sorting.
export const STATUS_TEXT = {
  open: "Open",
  implemented: "Satisfied",
  partial: "Partial",
  exception: "Risk Accepted",
  pending: "Pending",
  planned: "Planned",
  managed_solution: "Customer Managed",
  "in-progress": "In Progress",
  deferred: "Deferred",
  not_assessed: "Not Assessed",
};
export const statusText = (key) => STATUS_TEXT[key] || STATUS_TEXT.open;

// ── Derivations from a raw scored_settings row ──
// Both mirror the recs mapping in SecureShield365.js, which now calls these.

// Workflow status: what the customer has to DO about the row.
export function workflowStatusOf(s) {
  let status = "open";
  if (s.result === "Passed") status = "implemented";
  else if (s.result === "Failed") status = "open";
  // Partial derivation: backend awarded some but not all points.
  if (typeof s.earned === "number" && typeof s.max_possible === "number"
      && s.earned > 0 && s.earned < s.max_possible) {
    status = "partial";
  }
  return status;
}

// Derived assessment outcome (separate from workflow status above).
// scored_status is checked BEFORE result: an advisory/N-A setting can have a
// "Failed" result underneath and must still resolve accordingly.
export function verdictOf(s) {
  if (s.scored_status === "advisory") return "advisory";
  if (s.scored_status === "not_applicable") return "not_assessed";
  if (s.scored_status === "evaluation_failed") return "not_assessed";
  if (s.result === "Skipped" || s.result === "Error" || s.result === "NotRun") return "not_assessed";
  if (s.result === "Passed") return "pass";
  if (s.result === "Failed") return "fail";
  return "not_assessed";
}

// A scored row's EFFECTIVE status key, given its override record (or null).
// Precedence mirrors the recs-table statusCell exactly:
//   active override → Not Assessed verdict → workflow status.
export function effectiveStatusKeyOf(s, override) {
  const fromOverride = OVERRIDE_STATUS_KEY[override?.override];
  if (fromOverride) return fromOverride;
  if (verdictOf(s) === "not_assessed") return "not_assessed";
  return workflowStatusOf(s);
}
